import { useState, FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d71df2c991ove783stk0';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    if (name === 'message') setCharCount(value.length);
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (charCount > 500) return;
    setStatus('sending');

    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([key, val]) => body.append(key, val));

      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="kontakt" className="py-28 bg-white">
      <div className="max-w-7xl mx-auto px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="w-8 h-px bg-[#1A3A52]" />
              <span className="text-[#1A3A52] text-sm font-medium tracking-widest uppercase">
                Kontakt
              </span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-[#1A3A52] leading-tight mb-6">
              Sprechen wir über{' '}
              <span className="text-amber-500 italic font-light">Ihr Anliegen</span>
            </h2>
            <p className="text-stone-600 text-lg leading-relaxed mb-10">
              Nehmen Sie Kontakt auf – ich melde mich zeitnah bei Ihnen zurück und berate Sie
              zu Ihrem rechtlichen Anliegen im Bereich Sportrecht oder Medizinrecht.
            </p>

            <div className="flex flex-col gap-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#1A3A52]/10 shrink-0">
                  <i className="ri-map-pin-line text-[#1A3A52] text-xl" />
                </div>
                <div>
                  <p className="font-semibold text-[#1A3A52] mb-1">Kanzlei</p>
                  <p className="text-stone-500 text-sm">Kröpeliner Str. 54</p>
                  <p className="text-stone-500 text-sm">18055 Rostock</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#1A3A52]/10 shrink-0">
                  <i className="ri-phone-line text-[#1A3A52] text-xl" />
                </div>
                <div>
                  <p className="font-semibold text-[#1A3A52] mb-1">Telefon</p>
                  <a
                    href="tel:+491772506951"
                    className="text-amber-600 font-medium hover:text-amber-700 transition-colors"
                  >
                    +49 (177) 250 69 51
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#1A3A52]/10 shrink-0">
                  <i className="ri-time-line text-[#1A3A52] text-xl" />
                </div>
                <div>
                  <p className="font-semibold text-[#1A3A52] mb-1">Erreichbarkeit</p>
                  <p className="text-stone-500 text-sm">Mo–Fr: 08:00 – 18:00 Uhr</p>
                  <p className="text-stone-500 text-sm">Telefonisch oder vor Ort in Rostock</p>
                </div>
              </div>
            </div>

            <div className="mt-10 rounded-2xl overflow-hidden h-52">
              <iframe
                title="Kanzlei Dr. Dan Oliver Höftmann – Kröpeliner Str. 54, Rostock"
                width="100%"
                height="100%"
                loading="lazy"
                style={{ border: 0 }}
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2336.5474456!2d12.1315!3d54.0887!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47ac56d5da9c6f73%3A0x1234567890!2sKr%C3%B6peliner+Str.+54%2C+18055+Rostock!5e0!3m2!1sde!2sde!4v1234567890"
              />
            </div>
          </div>

          <div className="bg-[#F5F1E8] rounded-3xl p-8">
            <h3 className="text-xl font-bold text-[#1A3A52] mb-6">Nachricht senden</h3>

            {status === 'success' ? (
              <div className="flex flex-col items-center justify-center py-12 text-center gap-4">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100">
                  <i className="ri-check-line text-green-600 text-3xl" />
                </div>
                <p className="text-[#1A3A52] font-bold text-lg">Nachricht erfolgreich gesendet!</p>
                <p className="text-stone-500 text-sm">
                  Vielen Dank für Ihre Anfrage. Dr. Höftmann meldet sich zeitnah bei Ihnen.
                </p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-2 px-6 py-2 bg-[#1A3A52] text-white rounded-full text-sm font-medium cursor-pointer whitespace-nowrap"
                >
                  Neue Anfrage
                </button>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5 block">
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Ihr vollständiger Name"
                      className="w-full px-4 py-3 bg-white border border-stone-200 rounded-xl text-sm text-stone-800 focus:outline-none focus:border-[#1A3A52] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5 block">
                      E-Mail *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="ihre@email.de"
                      className="w-full px-4 py-3 bg-white border border-stone-200 rounded-xl text-sm text-stone-800 focus:outline-none focus:border-[#1A3A52] transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5 block">
                    Telefon
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+49 ..."
                    className="w-full px-4 py-3 bg-white border border-stone-200 rounded-xl text-sm text-stone-800 focus:outline-none focus:border-[#1A3A52] transition-colors"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5 block">
                    Rechtsgebiet *
                  </label>
                  <select
                    name="subject"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-stone-200 rounded-xl text-sm text-stone-800 focus:outline-none focus:border-[#1A3A52] transition-colors cursor-pointer"
                  >
                    <option value="">Bitte wählen...</option>
                    <option value="Medizinrecht">Medizinrecht</option>
                    <option value="Sportrecht">Sportrecht</option>
                    <option value="Arzthaftungsrecht">Arzthaftungsrecht</option>
                    <option value="Sportvertragsrecht">Sportvertragsrecht</option>
                    <option value="Sonstiges">Sonstiges</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5 block">
                    Ihr Anliegen *
                  </label>
                  <textarea
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    maxLength={500}
                    placeholder="Beschreiben Sie kurz Ihr rechtliches Anliegen..."
                    className="w-full px-4 py-3 bg-white border border-stone-200 rounded-xl text-sm text-stone-800 focus:outline-none focus:border-[#1A3A52] transition-colors resize-none"
                  />
                  <p className={`text-xs mt-1 text-right ${charCount > 450 ? 'text-red-500' : 'text-stone-400'}`}>
                    {charCount}/500 Zeichen
                  </p>
                </div>

                {status === 'error' && (
                  <p className="text-red-500 text-sm text-center">
                    Fehler beim Senden. Bitte versuchen Sie es erneut oder rufen Sie an.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === 'sending' || charCount > 500}
                  className="w-full py-4 bg-[#1A3A52] text-white font-bold rounded-xl text-sm tracking-wide hover:bg-[#0f2638] transition-all cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {status === 'sending' ? 'Wird gesendet...' : 'Nachricht absenden'}
                </button>

                <p className="text-xs text-stone-400 text-center">
                  Ihre Daten werden vertraulich behandelt und nicht an Dritte weitergegeben.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
